import type { RouteRecordRaw } from 'vue-router'
import { ROLE_GROUPS } from '../../constants/roleGroups'

const PlaceholderView = () => import('../../views/admin/stubs/AdminPlaceholderView.vue')

function appPlaceholder(
  path: string,
  name: string,
  title: string,
  todo: string[],
  roles = ROLE_GROUPS.ADMIN_PANEL,
  requiredAnyPermissions: string[] = [],
): RouteRecordRaw {
  return {
    path,
    name,
    component: PlaceholderView,
    meta: {
      title,
      requiresAuth: true,
      requireRoles: roles,
      ...(requiredAnyPermissions.length ? { requireAnyPermissions: requiredAnyPermissions } : {}),
      stubMessage: `${title} is scaffolded for the multi-application BORAsuite Admin shell.`,
      todo,
    },
  }
}

export const adminApplicationRoutes: RouteRecordRaw[] = [
  appPlaceholder('apps/pos/tenants', 'admin.apps.pos.tenants', 'POS Tenants', [
    'List POS tenants and plan status.',
    'Show tenancy health and activation state.',
    'Link tenant profile to billing and support.',
  ]),
  appPlaceholder('apps/pos/products', 'admin.apps.pos.products', 'POS Products', [
    'Expose product catalog controls by tenant.',
    'Add stock and pricing overviews.',
    'Wire product import/export workflows.',
  ]),
  appPlaceholder('apps/pos/customers', 'admin.apps.pos.customers', 'POS Customers', [
    'Track customer growth and account status.',
    'Add customer lifecycle actions.',
    'Surface customer activity and support history.',
  ]),

  appPlaceholder('apps/invoice/tenants', 'admin.apps.invoice.tenants', 'Invoice Tenants', [
    'List invoice tenants and account owners.',
    'Filter by subscription and payment status.',
    'Connect tenant details to documents and payments.',
  ]),
  appPlaceholder('apps/invoice/documents', 'admin.apps.invoice.documents', 'Invoice Documents', [
    'Show generated and pending invoice documents.',
    'Add lifecycle states and approvals.',
    'Link documents to account billing history.',
  ]),
  appPlaceholder('apps/invoice/payments', 'admin.apps.invoice.payments', 'Invoice Payments', [
    'Visualize payment events and failures.',
    'Add manual reconciliation controls.',
    'Connect failed payments to support actions.',
  ]),
  appPlaceholder('apps/invoice/expenses', 'admin.apps.invoice.expenses', 'Invoice Expenses', [
    'Track expense entries and categories.',
    'Add approval workflow controls.',
    'Aggregate monthly and annual spend summaries.',
  ]),

  appPlaceholder('apps/member/members', 'admin.apps.member.members', 'Member Directory', [
    'List members across active tenant accounts.',
    'Support status filtering and account search.',
    'Expose account and role snapshots per member.',
  ]),
  appPlaceholder('apps/member/groups', 'admin.apps.member.groups', 'Member Groups', [
    'Create and manage member groups.',
    'Track group membership trends.',
    'Link groups to events and activity history.',
  ]),
  appPlaceholder('apps/member/events', 'admin.apps.member.events', 'Member Events', [
    'Manage event schedules and registrations.',
    'Track attendance and engagement metrics.',
    'Bridge events into recent activity timeline.',
  ]),
  {
    path: 'apps/pharma/overview',
    name: 'admin.apps.pharma.overview',
    component: () => import('../../views/admin/apps/pharma/PharmaOverviewView.vue'),
    meta: {
      title: 'BORApharma Overview',
      requiresAuth: true,
      requireAnyPermissions: ['admin.borapharma.view', 'pharma_config.view'],
    },
  },
  {
    path: 'apps/pharma/tenants-users',
    name: 'admin.apps.pharma.tenants-users',
    component: () => import('../../views/admin/apps/pharma/PharmaTenantsUsersView.vue'),
    meta: {
      title: 'BORApharma Tenants / Users',
      requiresAuth: true,
      requireAnyPermissions: ['admin.borapharma.view', 'pharma_config.view', 'admin.clients.view'],
    },
  },
  {
    path: 'apps/pharma/config',
    name: 'admin.apps.pharma.config',
    component: () => import('../../views/admin/apps/pharma/PharmaConfigView.vue'),
    meta: {
      title: 'BORApharma Config',
      requiresAuth: true,
      requireAnyPermissions: ['admin.borapharma.view', 'pharma_config.view'],
    },
  },
  {
    path: 'apps/pharma/medicine-import',
    name: 'admin.apps.pharma.medicine-import',
    component: () => import('../../views/admin/apps/pharma/PharmaMasterListImportView.vue'),
    meta: {
      title: 'BORApharma — Medicine Master List Import',
      requiresAuth: true,
      requireAnyPermissions: ['admin.borapharma.manage', 'pharma_config.create'],
    },
  },
  {
    path: 'apps/pharma/insurance-companies',
    name: 'admin.apps.pharma.insurance-companies',
    component: () => import('../../views/admin/apps/pharma/InsuranceCompaniesView.vue'),
    meta: {
      title: 'Patient Care — Insurance Companies',
      requiresAuth: true,
      requireAnyPermissions: ['admin.patient_care.view', 'admin.patient_care.manage', 'admin.borapharma.view'],
    },
  },
  {
    path: 'shared-directory/products',
    name: 'admin.shared-directory.products',
    component: () => import('../../views/admin/shared-directory/SharedDirectoryView.vue'),
    props: { initialTab: 'products' },
    meta: {
      title: 'Shared Directory - Master Products',
      requiresAuth: true,
      requireAnyPermissions: ['shared_directory.view'],
    },
  },
  {
    path: 'shared-directory/product-images',
    name: 'admin.shared-directory.product-images',
    component: () => import('../../views/admin/shared-directory/SharedDirectoryView.vue'),
    props: { initialTab: 'product_images' },
    meta: {
      title: 'Shared Directory - Product Images',
      requiresAuth: true,
      requireAnyPermissions: ['shared_directory.view'],
    },
  },
  {
    path: 'shared-directory/product-variants',
    name: 'admin.shared-directory.product-variants',
    component: () => import('../../views/admin/shared-directory/SharedDirectoryView.vue'),
    props: { initialTab: 'product_variants' },
    meta: {
      title: 'Shared Directory - Product Variants',
      requiresAuth: true,
      requireAnyPermissions: ['shared_directory.view'],
    },
  },
  {
    path: 'shared-directory/product-types',
    name: 'admin.shared-directory.product-types',
    component: () => import('../../views/admin/shared-directory/SharedDirectoryView.vue'),
    props: { initialTab: 'product_types' },
    meta: {
      title: 'Shared Directory - Product Types',
      requiresAuth: true,
      requireAnyPermissions: ['shared_directory.view'],
    },
  },
  {
    path: 'shared-directory/product-categories',
    name: 'admin.shared-directory.product-categories',
    component: () => import('../../views/admin/shared-directory/SharedDirectoryView.vue'),
    props: { initialTab: 'product_categories' },
    meta: {
      title: 'Shared Directory - Product Categories',
      requiresAuth: true,
      requireAnyPermissions: ['shared_directory.view'],
    },
  },
  {
    path: 'shared-directory/brands',
    name: 'admin.shared-directory.brands',
    component: () => import('../../views/admin/shared-directory/SharedDirectoryView.vue'),
    props: { initialTab: 'brands' },
    meta: {
      title: 'Shared Directory - Brands',
      requiresAuth: true,
      requireAnyPermissions: ['shared_directory.view'],
    },
  },
  {
    path: 'shared-directory/units',
    name: 'admin.shared-directory.units',
    component: () => import('../../views/admin/shared-directory/SharedDirectoryView.vue'),
    props: { initialTab: 'units' },
    meta: {
      title: 'Shared Directory - Units',
      requiresAuth: true,
      requireAnyPermissions: ['shared_directory.view'],
    },
  },
  {
    path: 'shared-directory/country-data',
    name: 'admin.shared-directory.country-data',
    component: () => import('../../views/admin/shared-directory/SharedDirectoryView.vue'),
    props: { initialTab: 'country_data' },
    meta: {
      title: 'Shared Directory - Countries / Local Data',
      requiresAuth: true,
      requireAnyPermissions: ['shared_directory.view'],
    },
  },
]
